import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetSkuLabelV1TaxonomySkusPlatformProductIdGetQueryKey,
  getListNodesV1TaxonomyNodesGetQueryKey,
  getReviewSkusV1TaxonomySkusGetQueryKey,
  useClearOverrideV1TaxonomySkusPlatformProductIdOverrideDelete,
  useGetSkuLabelV1TaxonomySkusPlatformProductIdGet,
  useListNodesV1TaxonomyNodesGet,
  useReclassifySkuV1TaxonomySkusPlatformProductIdReclassifyPost,
  useSetOverrideV1TaxonomySkusPlatformProductIdOverridePost,
} from "../api/generated/qcsControlPlane";
import type { Platform, TaxonomyNodeOut } from "../api/generated/model";
import { Button, Input, Panel, Row, Stack } from "@akaike/ui";
import { useCanWrite } from "../auth/me";
import { useToast } from "./toast";

function errMsg(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
  }
  return fallback;
}

// Full " > " path for a node, walking parents.
function pathOf(id: string, byId: Map<string, TaxonomyNodeOut>): string {
  const names: string[] = [];
  let cur: TaxonomyNodeOut | undefined = byId.get(id);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    names.unshift(cur.name);
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
  }
  return names.join(" > ");
}

export function TaxonomyPanel({
  platform,
  productId,
  embedded = false,
}: {
  platform: Platform;
  productId: string;
  // Rendered as a sub-section of another panel: body only, no Panel wrapper or head
  // (panels must never nest).
  embedded?: boolean;
}) {
  const canWrite = useCanWrite();
  const notify = useToast();
  const qc = useQueryClient();
  const encoded = encodeURIComponent(productId);

  const labelQ = useGetSkuLabelV1TaxonomySkusPlatformProductIdGet(platform, encoded);
  const nodesQ = useListNodesV1TaxonomyNodesGet();
  const label = labelQ.data;

  const [picking, setPicking] = useState(false);
  const [filter, setFilter] = useState("");

  const nodes = useMemo(() => nodesQ.data ?? [], [nodesQ.data]);
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n] as const)), [nodes]);
  // Mirror the backend's leaf rule (taxonomy_repo.leaves_from_nodes) exactly, so the
  // picker only offers nodes that set_override will accept: a leaf is active, has no
  // *active* children, and every ancestor is active. Counting inactive children (or
  // skipping the ancestor check) would either hide valid targets or offer ones the
  // backend rejects with 422.
  const activeChildParents = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes) if (n.is_active && n.parent_id) s.add(n.parent_id);
    return s;
  }, [nodes]);
  const ancestorsActive = useMemo(() => {
    const cache = new Map<string, boolean>();
    const check = (id: string): boolean => {
      const cached = cache.get(id);
      if (cached !== undefined) return cached;
      const n = byId.get(id);
      // Only ancestors must be active; the node's own is_active is checked separately.
      const ok = !n?.parent_id || (byId.get(n.parent_id)?.is_active === true && check(n.parent_id));
      cache.set(id, ok);
      return ok;
    };
    return check;
  }, [byId]);
  const leaves = useMemo(
    () =>
      nodes
        .filter((n) => n.is_active && !activeChildParents.has(n.id) && ancestorsActive(n.id))
        .map((n) => ({ id: n.id, path: pathOf(n.id, byId) }))
        .sort((a, b) => a.path.localeCompare(b.path)),
    [nodes, activeChildParents, ancestorsActive, byId],
  );
  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const base = f ? leaves.filter((l) => l.path.toLowerCase().includes(f)) : leaves;
    return base.slice(0, 60);
  }, [leaves, filter]);

  const invalidate = () => {
    qc.invalidateQueries({
      queryKey: getGetSkuLabelV1TaxonomySkusPlatformProductIdGetQueryKey(platform, encoded),
    });
    // Correcting one SKU changes the review queue and per-node SKU counts too; refresh
    // both now instead of waiting for their own poll interval. Match on the endpoint
    // prefix (first key element) so every filter/page variant is invalidated.
    qc.invalidateQueries({
      queryKey: [getReviewSkusV1TaxonomySkusGetQueryKey({ platform })[0]],
    });
    qc.invalidateQueries({ queryKey: getListNodesV1TaxonomyNodesGetQueryKey() });
  };

  const setOverride = useSetOverrideV1TaxonomySkusPlatformProductIdOverridePost({
    mutation: {
      onSuccess: () => {
        invalidate();
        setPicking(false);
        notify("Override set");
      },
      onError: (e) => notify(errMsg(e, "Override failed"), "crit"),
    },
  });
  const clearOverride = useClearOverrideV1TaxonomySkusPlatformProductIdOverrideDelete({
    mutation: {
      onSuccess: () => {
        invalidate();
        notify("Override cleared, reclassified");
      },
      onError: (e) => notify(errMsg(e, "Clear failed"), "crit"),
    },
  });
  const reclassify = useReclassifySkuV1TaxonomySkusPlatformProductIdReclassifyPost({
    mutation: {
      onSuccess: () => {
        invalidate();
        notify("Reclassified");
      },
      onError: (e) => notify(errMsg(e, "Reclassify failed"), "crit"),
    },
  });

  const busy = setOverride.isPending || clearOverride.isPending || reclassify.isPending;
  const isOverride = label?.source === "override";
  const path = label?.leaf_path || "";

  const body = (
        <Stack gap="sm">
        {labelQ.isLoading ? (
          <span className="sub">loading...</span>
        ) : (
          <>
            <div>
              {path ? (
                <div className="mono" style={{ fontSize: 13 }}>
                  {path}
                </div>
              ) : (
                <div className="sub">
                  {label?.source === "unmapped" ? "(unmapped - no fit)" : "not classified yet"}
                </div>
              )}
              <div className="sub" style={{ marginTop: 3 }}>
                <span className={`taxo-src taxo-src--${label?.source ?? "none"}`}>
                  {label?.source ?? "none"}
                </span>
                {label && label.source !== "none" && (
                  <>
                    {" "}
                    &middot; confidence {label.confidence.toFixed(2)}
                  </>
                )}
              </div>
              {label?.reasoning && (
                <div className="sub" style={{ marginTop: 5 }}>
                  <span className="eyebrow" style={{ fontSize: 9.5, marginRight: 6 }}>
                    Reasoning
                  </span>
                  {label.reasoning}
                </div>
              )}
            </div>

            {canWrite && <Row gap="sm" wrap>
              <Button
                size="sm"
                onClick={() => setPicking((p) => !p)}
                disabled={busy}
              >
                {isOverride ? "Change override" : "Set override"}
              </Button>
              {isOverride ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => clearOverride.mutate({ platform, productId: encoded })}
                  disabled={busy}
                >
                  Clear override
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reclassify.mutate({ platform, productId: encoded })}
                  disabled={busy}
                >
                  Reclassify
                </Button>
              )}
            </Row>}

            {canWrite && picking && (
              <Stack gap="sm">
                <Input
                  placeholder="Search leaf categories..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  autoFocus
                />
                <div
                  style={{
                    maxHeight: 220,
                    overflow: "auto",
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                  }}
                >
                  {shown.map((l) => (
                    <button
                      type="button"
                      key={l.id}
                      className="as-btn taxo-pick"
                      onClick={() =>
                        setOverride.mutate({
                          platform,
                          productId: encoded,
                          data: { node_id: l.id, reason: "manual override" },
                        })
                      }
                    >
                      {l.path}
                    </button>
                  ))}
                  {shown.length === 0 && (
                    <div className="sub" style={{ padding: 8 }}>
                      no matching leaves
                    </div>
                  )}
                </div>
              </Stack>
            )}
          </>
        )}
        </Stack>
  );
  if (embedded) return body;
  return (
    <Panel>
      <Panel.Head>
        <b>Taxonomy</b>
      </Panel.Head>
      <Panel.Body>{body}</Panel.Body>
    </Panel>
  );
}
